import * as React from 'react';
import { upperFirst, escapeRegExp, uniq } from 'lodash';
import { TEAMSNAP_URL } from 'core/constants';
import { Link } from 'react-router-dom';
import { currencyGivenPennies } from 'core';

interface FinancialLinkInfo {
  rootDivisionId: number;
  batchInvoiceId: number;
  invoiceId: number;
  registrationFormId: number;
  registrationId: number;
  orderNumber: string;
  organizationId: number;
}

export interface SourceItem {
  divisionId: number;
  divisionName: string;
  linkInfo: [FinancialLinkInfo];
  participantFirstName: string;
  participantLastName: string;
  programSeason: string;
  seasonId: number;
  sourceName: string;
  teamId: number;
  teamName: string;
}

function buildItemSourceUrl(
  FinancialLinkInfo: FinancialLinkInfo,
  type
): any | null {
  if (type === 'Registration') {
    return buildBaseRegistrationUrl(
      FinancialLinkInfo.rootDivisionId,
      FinancialLinkInfo.registrationFormId
    );
  } else if (type === 'Invoice') {
    return buildBaseInvoiceUrl(
      FinancialLinkInfo.rootDivisionId,
      FinancialLinkInfo.batchInvoiceId
    );
  } else if (type === 'RegistrationV2') {
    return buildFormResultsUrl(
      FinancialLinkInfo.organizationId,
      FinancialLinkInfo.registrationFormId
    );
  }
  return null;
}

function buildBaseRegistrationUrl(
  divisionId: number,
  registrationFormId: number
): string {
  return registrationFormId
    ? `${TEAMSNAP_URL}/divisions/${divisionId}/registration/${registrationFormId}`
    : '#';
}

function buildBaseInvoiceUrl(
  divisionId: number,
  batchInvoiceId: number
): string {
  return batchInvoiceId
    ? `${TEAMSNAP_URL}/${divisionId}/league_payments/invoicing/${batchInvoiceId}/invoices`
    : '#';
}

function buildFormResultsUrl(organizationId, formId) {
  return formId
    ? `/organizations/${organizationId}/registration/${formId}/details`
    : '#';
}

export const sourceNameLinksForSourceItems = (sourceItems) => {
  const uniqSources = sourceItems.reduce((acc, x) => {
    const sourceUrl = buildItemSourceUrl(x.linkInfo, x.type);

    if (!acc.map((v) => v.sourceUrl).includes(sourceUrl)) {
      acc.push({ name: x.sourceName, sourceUrl: sourceUrl });
    }

    return acc;
  }, []);

  return (
    <>
      {uniqSources.map((sourceInfo) => {
        if (sourceInfo.sourceUrl?.startsWith('/')) {
          return <Link to={sourceInfo.sourceUrl}>{sourceInfo.name}</Link>;
        } else {
          return <a href={sourceInfo.sourceUrl}>{sourceInfo.name}</a>;
        }
      })}
    </>
  );
};

export const joinNamesByComma = (sourceItems) =>
  sourceItems
    .map((x) =>
      `${x.participantFirstName || ''} ${x.participantLastName || ''}`.trim()
    )
    .filter((x) => !!x)
    .join(', ');

export const formatProgramNames = (sourceItems) =>
  uniq(sourceItems.map((x) => upperFirst(x.programSeason))).join();

export const sourceNameForSourceItems = (sourceItems) =>
  sourceItems
    .reduce((acc, x) => {
      if (!acc.includes(x.sourceName)) {
        acc.push(x.sourceName);
      }
      return acc;
    }, [])
    .join();

export const orderNumberMatch = (orderNumber: string, searchTerm: string) => {
  if (!orderNumber) return false;
  const searchTermRegExp = new RegExp(escapeRegExp(searchTerm), 'i');
  return orderNumber.match(searchTermRegExp) !== null;
};

export const depositIdMatch = (depositId: string, searchTerm: string) => {
  if (!depositId) return false;
  const searchTermRegExp = new RegExp(escapeRegExp(searchTerm), 'i');
  return depositId.match(searchTermRegExp) !== null;
};

export const memberMatch = (members: string, searchTerm: string) => {
  const searchTermRegExp = new RegExp(escapeRegExp(searchTerm), 'i');
  return members.match(searchTermRegExp) !== null;
};

export const sourceMatch = (sourceName: string, searchTerm: string) => {
  const searchTermRegExp = new RegExp(escapeRegExp(searchTerm), 'i');
  return sourceName.match(searchTermRegExp) !== null;
};

export const beginningOfDay = (date: Date) =>
  new Date(date.setHours(0, 0, 0, 0));
export const endOfDay = (date: Date) =>
  new Date(date.setHours(23, 59, 59, 999));
export const previousDayEndOfDay = (date: Date, timezoneOffset) => {
  const previousDay = new Date(date.getTime());
  previousDay.setDate(date.getDate() - 1);
  return new Date(previousDay.setHours(23 + timezoneOffset, 59, 59, 999));
};

export const orgDateTimeFromUTCDate = (orgTimezone: string, date: Date) => {
  const offset = new Date(
    new Date().toLocaleString('en-US', { timeZone: orgTimezone })
  ).getTimezoneOffset();
  const newDate = new Date(date.getTime() - offset);

  return newDate;
};

export const convertAmountAsPennies = (amount) => {
  if (!amount || amount[0] === '$') return amount;

  return currencyGivenPennies(+amount);
};

export const applySearchTermToItems = (items: any[], searchTerm) => {
  if (!searchTerm) return items;
  return items.filter(
    (x) =>
      orderNumberMatch(x.orderNumber, searchTerm) ||
      memberMatch(joinNamesByComma(x.sourceItems), searchTerm) ||
      depositIdMatch(x.depositId, searchTerm) ||
      x.sourceItems.filter((sourceItem) =>
        sourceMatch(sourceItem.sourceName, searchTerm)
      ).length > 0
  );
};
